// Bidirectional mapping between integration display names (shown in the UI
// integration filter dropdown) and the raw IntegrationType enum names stored
// in badge-log's database. The badge-log API expects the enum form for any
// `integration_name` filter, while the UI shows the display form.
//
// Two call sites need to agree on this mapping — the access-events table and
// the person-search autocomplete (EIP-1540). Keeping both maps and helpers
// here ensures they stay in sync.

export const INTEGRATION_DISPLAY_NAME_TO_TYPE: Record<string, string> = {
  Unknown: 'INTEGRATION_TYPE_UNSPECIFIED',
  CCURE: 'INTEGRATION_TYPE_CCURE',
  OnGuard: 'INTEGRATION_TYPE_ONGUARD',
  NetBox: 'INTEGRATION_TYPE_NETBOX',
  Brivo: 'INTEGRATION_TYPE_BRIVO',
  Kisi: 'INTEGRATION_TYPE_KISI',
  Kantech: 'INTEGRATION_TYPE_KANTECH',
  Genetec: 'INTEGRATION_TYPE_GENETEC',
  'Avigilon Alta': 'INTEGRATION_TYPE_OPENPATH',
  'Avigilon Unity': 'INTEGRATION_TYPE_AVIGILON_UNITY',
  Meraki: 'INTEGRATION_TYPE_CISCO_MERAKI',
  'Aruba Central': 'INTEGRATION_TYPE_ARUBA_CENTRAL',
  'Aruba Clearpass': 'INTEGRATION_TYPE_ARUBA_CLEARPASS',
  Unifi: 'INTEGRATION_TYPE_UNIFI',
  Mist: 'INTEGRATION_TYPE_MIST',
  Datawatch: 'INTEGRATION_TYPE_DATAWATCH',
  Gallagher: 'INTEGRATION_TYPE_GALLAGHER',
  Feenics: 'INTEGRATION_TYPE_FEENICS',
  'Paxton Net2': 'INTEGRATION_TYPE_PAXTON_NET2',
  Protogegx: 'INTEGRATION_TYPE_PROTEGEGX',
  Verkada: 'INTEGRATION_TYPE_VERKADA',
  'Envoy Mobile': 'INTEGRATION_TYPE_ENVOY_MOBILE',
  Genea: 'INTEGRATION_TYPE_GENEA',
  Okta: 'INTEGRATION_TYPE_OKTA',
  'Entra ID': 'INTEGRATION_TYPE_ENTRA_ID',
  Jamf: 'INTEGRATION_TYPE_JAMF',
  Meter: 'INTEGRATION_TYPE_METER',
};

export const INTEGRATION_TYPE_TO_DISPLAY_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(INTEGRATION_DISPLAY_NAME_TO_TYPE).map(([display, type]) => [type, display]),
);

/**
 * Map a list of UI display names to the enum forms badge-log expects in its
 * `integration_name` filter. Unknown display names pass through unchanged so
 * the caller can also forward already-mapped values without double-mapping.
 */
export function toIntegrationTypeNames(displayNames: readonly string[]): string[] {
  return displayNames.map((name) => INTEGRATION_DISPLAY_NAME_TO_TYPE[name] ?? name);
}

/**
 * Inverse of {@link toIntegrationTypeNames}: turn enum names returned by the
 * API into human-readable display labels for the integration filter dropdown.
 */
export function toIntegrationDisplayNames(typeNames: readonly string[]): string[] {
  return typeNames.map((type) => INTEGRATION_TYPE_TO_DISPLAY_NAME[type] ?? type);
}
